import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { openAuthenticatedFile } from '@/lib/api';
import { 
  Users, ClipboardCheck, Star, FileText, Calendar, 
  ChevronDown, ChevronUp, Plus, Trash2, Check, X,
  Clock, AlertCircle, Download, Eye, Loader2, Shield, Send
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HRManagement = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [onboardingProgress, setOnboardingProgress] = useState(null);
  const [onboardingTasks, setOnboardingTasks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [policySignatures, setPolicySignatures] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);

  // Forms
  const [showNewReview, setShowNewReview] = useState(false);
  const [showNewHoliday, setShowNewHoliday] = useState(false);
  const [newReview, setNewReview] = useState({
    employee_id: '',
    review_period_start: '',
    review_period_end: '',
    overall_rating: 3,
    strengths: '',
    areas_for_improvement: '',
    goals_for_next_period: '',
    comments: ''
  });
  const [newHoliday, setNewHoliday] = useState({
    name: '',
    date: '',
    end_date: '',
    description: '',
    is_office_closed: true,
    is_multi_day: false
  });

  const token = localStorage.getItem('token');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchStats();
    fetchEmployees();
    fetchOnboardingTasks();
    fetchReviews();
    fetchHolidays();
    fetchExpiringDocuments();
    fetchPolicySignatures();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/dashboard/stats`, { headers });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API_URL}/api/employees`, { headers });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchOnboardingTasks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/onboarding/tasks`, { headers });
      if (res.ok) {
        const data = await res.json();
        setOnboardingTasks(data);
      }
    } catch (error) {
      console.error('Error fetching onboarding tasks:', error);
    }
  };

  const fetchEmployeeOnboarding = async (employeeId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/hr/onboarding/employee/${employeeId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setOnboardingProgress(data);
      }
    } catch (error) {
      console.error('Error fetching onboarding:', error);
    }
    setLoading(false);
  };

  const toggleOnboardingTask = async (employeeId, taskId, currentStatus) => {
    try {
      const res = await fetch(
        `${API_URL}/api/hr/onboarding/employee/${employeeId}/task/${taskId}?completed=${!currentStatus}`,
        { method: 'PUT', headers }
      );
      if (res.ok) {
        fetchEmployeeOnboarding(employeeId);
        fetchStats();
        toast.success(`Task ${!currentStatus ? 'completed' : 'marked incomplete'}`);
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const fetchReviews = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/reviews`, { headers });
      if (res.ok) {
        const data = await res.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const createReview = async () => {
    if (!newReview.employee_id || !newReview.review_period_start || !newReview.review_period_end) {
      toast.error('Please fill required fields');
      return;
    }
    
    setLoading(true);
    try {
      const userId = JSON.parse(localStorage.getItem('user'))?.id;
      const payload = { ...newReview, reviewer_id: userId };
      
      const res = await fetch(`${API_URL}/api/hr/reviews`, {
        method: 'POST',
        headers,
        
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Review created');
        setShowNewReview(false);
        setNewReview({
          employee_id: '',
          review_period_start: '',
          review_period_end: '',
          overall_rating: 3,
          strengths: '',
          areas_for_improvement: '',
          goals_for_next_period: '',
          comments: ''
        });
        fetchReviews();
        fetchStats();
      } else {
        toast.error('Failed to create review');
      }
    } catch (error) {
      toast.error('Error creating review');
    }
    setLoading(false);
  };

  const submitReview = async (reviewId) => {
    try {
      const res = await fetch(`${API_URL}/api/hr/reviews/${reviewId}?status=submitted`, {
        method: 'PUT',
        headers,
        
      });
      if (res.ok) {
        toast.success('Review submitted');
        fetchReviews();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      const res = await fetch(`${API_URL}/api/hr/reviews/${reviewId}`, {
        method: 'DELETE',
        headers,
        
      });
      if (res.ok) {
        toast.success('Review deleted');
        fetchReviews();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const fetchHolidays = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/holidays`, { headers });
      if (res.ok) {
        const data = await res.json();
        setHolidays(data);
      }
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const createHoliday = async () => {
    if (!newHoliday.name || !newHoliday.date) {
      toast.error('Please fill required fields');
      return;
    }
    
    // Validate date range if multi-day
    if (newHoliday.is_multi_day && !newHoliday.end_date) {
      toast.error('Please select an end date for multi-day holiday');
      return;
    }
    
    if (newHoliday.is_multi_day && newHoliday.end_date < newHoliday.date) {
      toast.error('End date must be after start date');
      return;
    }
    
    setLoading(true);
    try {
      // If multi-day, create multiple holidays (one for each day)
      if (newHoliday.is_multi_day && newHoliday.end_date) {
        const startDate = new Date(newHoliday.date);
        const endDate = new Date(newHoliday.end_date);
        const holidays = [];
        
        // Create a holiday for each day in the range
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          const dateStr = d.toISOString().split('T')[0];
          holidays.push({
            name: newHoliday.name,
            date: dateStr,
            description: newHoliday.description || `${newHoliday.name} (${newHoliday.date} to ${newHoliday.end_date})`,
            is_office_closed: newHoliday.is_office_closed
          });
        }
        
        // Create all holidays
        let successCount = 0;
        for (const holiday of holidays) {
          const res = await fetch(`${API_URL}/api/hr/holidays`, {
            method: 'POST',
            headers,
        
            body: JSON.stringify(holiday)
          });
          if (res.ok) successCount++;
        }
        
        if (successCount > 0) {
          toast.success(`${successCount} holiday dates added successfully!`);
          setShowNewHoliday(false);
          setNewHoliday({ name: '', date: '', end_date: '', description: '', is_office_closed: true, is_multi_day: false });
          fetchHolidays();
          fetchStats();
        } else {
          toast.error('Failed to create holidays');
        }
      } else {
        // Single day holiday
        const res = await fetch(`${API_URL}/api/hr/holidays`, {
          method: 'POST',
          headers,
        
          body: JSON.stringify({
            name: newHoliday.name,
            date: newHoliday.date,
            description: newHoliday.description,
            is_office_closed: newHoliday.is_office_closed
          })
        });
        
        if (res.ok) {
          toast.success('Holiday created');
          setShowNewHoliday(false);
          setNewHoliday({ name: '', date: '', end_date: '', description: '', is_office_closed: true, is_multi_day: false });
          fetchHolidays();
          fetchStats();
        } else {
          toast.error('Failed to create holiday');
        }
      }
    } catch (error) {
      toast.error('Error creating holiday');
    }
    setLoading(false);
  };

  const deleteHoliday = async (holidayId) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      const res = await fetch(`${API_URL}/api/hr/holidays/${holidayId}`, {
        method: 'DELETE',
        headers,
        
      });
      if (res.ok) {
        toast.success('Holiday deleted');
        fetchHolidays();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  const fetchExpiringDocuments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/documents/expiring/soon?days=60`, { headers });
      if (res.ok) {
        const data = await res.json();
        setExpiringDocs(data);
      }
    } catch (error) {
      console.error('Error fetching expiring docs:', error);
    }
  };

  const fetchEmployeeDocuments = async (employeeId) => {
    try {
      const res = await fetch(`${API_URL}/api/hr/documents/${employeeId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchPolicySignatures = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/policies/signatures`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPolicySignatures(data);
      }
    } catch (error) {
      console.error('Error fetching policy signatures:', error);
    }
  };

  const sendPolicyReminders = async (userIds) => {
    try {
      setSendingReminders(true);
      const res = await fetch(`${API_URL}/api/admin/policies/remind`, {
        method: 'POST',
        headers,
        
        body: JSON.stringify({ user_ids: userIds })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
      } else {
        toast.error('Failed to send reminders');
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Users },
    { id: 'onboarding', label: 'Onboarding', icon: ClipboardCheck },
    { id: 'reviews', label: 'Performance Reviews', icon: Star },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'holidays', label: 'Holiday Calendar', icon: Calendar },
    { id: 'policies', label: 'Policy Signatures', icon: Shield },
  ];

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-green-600 bg-green-100';
    if (rating >= 3) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      submitted: 'bg-blue-100 text-blue-700',
      acknowledged: 'bg-green-100 text-green-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6" data-testid="hr-management">
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b pb-2 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-teal-100 text-teal-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid={`hr-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="bg-white p-4 rounded-xl border shadow-sm">
              <p className="text-sm text-gray-500">Total Employees</p>
              <p className="text-2xl font-bold text-gray-800">{stats?.total_employees || 0}</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
              <p className="text-sm text-orange-600">Pending Onboarding</p>
              <p className="text-2xl font-bold text-orange-700">{stats?.pending_onboarding || 0}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
              <p className="text-sm text-blue-600">Pending Reviews</p>
              <p className="text-2xl font-bold text-blue-700">{stats?.pending_reviews || 0}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-xl border border-red-200">
              <p className="text-sm text-red-600">Expiring Documents</p>
              <p className="text-2xl font-bold text-red-700">{stats?.expiring_documents || 0}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
              <p className="text-sm text-purple-600">Upcoming Holidays</p>
              <p className="text-2xl font-bold text-purple-700">{stats?.upcoming_holidays || 0}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
              <p className="text-sm text-yellow-600">Leave Requests</p>
              <p className="text-2xl font-bold text-yellow-700">{stats?.pending_leave_requests || 0}</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white p-6 rounded-xl border shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={() => { setActiveTab('reviews'); setShowNewReview(true); }}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Performance Review
              </Button>
              <Button 
                onClick={() => { setActiveTab('holidays'); setShowNewHoliday(true); }}
                variant="outline"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Add Holiday
              </Button>
              <Button 
                onClick={() => setActiveTab('onboarding')}
                variant="outline"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                Check Onboarding
              </Button>
            </div>
          </div>

          {/* Expiring Documents Alert */}
          {expiringDocs.length > 0 && (
            <div className="bg-red-50 border border-red-200 p-4 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-red-700">Documents Expiring Soon</h3>
              </div>
              <div className="space-y-2">
                {expiringDocs.slice(0, 5).map(doc => (
                  <div key={doc.id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-800">{doc.document_name}</p>
                      <p className="text-sm text-gray-500">{doc.employee_name} - {doc.document_type}</p>
                    </div>
                    <span className="text-sm text-red-600 font-medium">Expires: {doc.expiry_date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Onboarding Tab */}
      {activeTab === 'onboarding' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl border shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Employee Onboarding Progress</h3>
            
            {/* Employee Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Employee</label>
              <Select
                value={selectedEmployee?.id || ''}
                onValueChange={(value) => {
                  const emp = employees.find(e => e.id === value);
                  setSelectedEmployee(emp);
                  if (emp) fetchEmployeeOnboarding(emp.id);
                }}
              >
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder="Select an employee..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.full_name} - {emp.position}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Onboarding Progress */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
              </div>
            ) : onboardingProgress ? (
              <div>
                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Progress</span>
                    <span className="font-medium text-teal-600">
                      {onboardingProgress.completed_tasks}/{onboardingProgress.total_tasks} tasks ({onboardingProgress.completion_percentage}%)
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-teal-500 transition-all duration-500"
                      style={{ width: `${onboardingProgress.completion_percentage}%` }}
                    />
                  </div>
                </div>

                {/* Tasks List */}
                <div className="space-y-3">
                  {onboardingProgress.tasks.map(task => (
                    <div 
                      key={task.id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        task.completed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => toggleOnboardingTask(onboardingProgress.employee_id, task.id, task.completed)}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                            task.completed 
                              ? 'bg-green-500 border-green-500 text-white' 
                              : 'border-gray-300 hover:border-teal-500'
                          }`}
                        >
                          {task.completed && <Check className="w-4 h-4" />}
                        </button>
                        <div>
                          <p className={`font-medium ${task.completed ? 'text-green-700 line-through' : 'text-gray-800'}`}>
                            {task.title}
                            {task.is_required && <span className="text-red-500 ml-1">*</span>}
                          </p>
                          <p className="text-sm text-gray-500 capitalize">{task.category}</p>
                        </div>
                      </div>
                      {task.completed_at && (
                        <span className="text-xs text-gray-400">
                          Completed: {new Date(task.completed_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Select an employee to view onboarding progress</p>
            )}
          </div>
        </div>
      )}

      {/* Performance Reviews Tab */}
      {activeTab === 'reviews' && (
        <div className="space-y-6">
          {/* New Review Form */}
          {showNewReview && (
            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">New Performance Review</h3>
                <button onClick={() => setShowNewReview(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                  <Select
                    value={newReview.employee_id}
                    onValueChange={(value) => setNewReview({...newReview, employee_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select employee..." />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Overall Rating *</label>
                  <Select
                    value={String(newReview.overall_rating)}
                    onValueChange={(value) => setNewReview({...newReview, overall_rating: parseInt(value)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map(r => (
                        <SelectItem key={r} value={String(r)}>
                          {r} - {r === 1 ? 'Poor' : r === 2 ? 'Below Average' : r === 3 ? 'Average' : r === 4 ? 'Good' : 'Excellent'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Period Start *</label>
                  <Input 
                    type="date"
                    value={newReview.review_period_start}
                    onChange={(e) => setNewReview({...newReview, review_period_start: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Period End *</label>
                  <Input 
                    type="date"
                    value={newReview.review_period_end}
                    onChange={(e) => setNewReview({...newReview, review_period_end: e.target.value})}
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Strengths *</label>
                  <textarea
                    className="w-full p-3 border rounded-lg"
                    rows={3}
                    value={newReview.strengths}
                    onChange={(e) => setNewReview({...newReview, strengths: e.target.value})}
                    placeholder="Key strengths demonstrated..."
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Areas for Improvement *</label>
                  <textarea
                    className="w-full p-3 border rounded-lg"
                    rows={3}
                    value={newReview.areas_for_improvement}
                    onChange={(e) => setNewReview({...newReview, areas_for_improvement: e.target.value})}
                    placeholder="Areas needing development..."
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Goals for Next Period</label>
                  <textarea
                    className="w-full p-3 border rounded-lg"
                    rows={2}
                    value={newReview.goals_for_next_period}
                    onChange={(e) => setNewReview({...newReview, goals_for_next_period: e.target.value})}
                    placeholder="Goals and objectives..."
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-4">
                <Button variant="outline" onClick={() => setShowNewReview(false)}>Cancel</Button>
                <Button onClick={createReview} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Review
                </Button>
              </div>
            </div>
          )}

          {/* Reviews List */}
          <div className="bg-white p-6 rounded-xl border shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Performance Reviews</h3>
              {!showNewReview && (
                <Button onClick={() => setShowNewReview(true)} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  New Review
                </Button>
              )}
            </div>
            
            {reviews.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No performance reviews yet</p>
            ) : (
              <div className="space-y-4">
                {reviews.map(review => (
                  <div key={review.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-800">{review.employee_name}</p>
                        <p className="text-sm text-gray-500">
                          {review.review_period_start} to {review.review_period_end}
                        </p>
                        <p className="text-sm text-gray-500">Reviewer: {review.reviewer_name}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(review.status)}`}>
                          {review.status}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-lg font-bold ${getRatingColor(review.overall_rating)}`}>
                          {review.overall_rating}/5
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-medium text-gray-700">Strengths:</p>
                        <p className="text-gray-600">{review.strengths.substring(0, 100)}...</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">Areas for Improvement:</p>
                        <p className="text-gray-600">{review.areas_for_improvement.substring(0, 100)}...</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      {review.status === 'draft' && (
                        <Button size="sm" onClick={() => submitReview(review.id)} className="bg-blue-600 hover:bg-blue-700">
                          Submit Review
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => deleteReview(review.id)}>
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl border shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Employee Documents</h3>
            
            {/* Employee Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Employee</label>
              <Select
                value={selectedEmployee?.id || ''}
                onValueChange={(value) => {
                  const emp = employees.find(e => e.id === value);
                  setSelectedEmployee(emp);
                  if (emp) fetchEmployeeDocuments(emp.id);
                }}
              >
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder="Select an employee..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.full_name} - {emp.position}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {documents.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                {selectedEmployee ? 'No documents found for this employee' : 'Select an employee to view documents'}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {documents.map(doc => (
                  <div key={doc.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start gap-3">
                      <FileText className="w-8 h-8 text-teal-600" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">{doc.document_name}</p>
                        <p className="text-sm text-gray-500 capitalize">{doc.document_type.replace('_', ' ')}</p>
                        {doc.expiry_date && (
                          <p className="text-sm text-orange-600">Expires: {doc.expiry_date}</p>
                        )}
                      </div>
                      <button 
                        onClick={() => openAuthenticatedFile(doc.file_url).catch(() => toast.error('Failed to open document'))}
                        className="text-teal-600 hover:text-teal-700 cursor-pointer"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Holidays Tab */}
      {activeTab === 'holidays' && (
        <div className="space-y-6">
          {/* New Holiday Form */}
          {showNewHoliday && (
            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Add Holiday</h3>
                <button onClick={() => setShowNewHoliday(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Holiday Type Selection */}
              <div className="flex gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="holiday_type"
                    checked={!newHoliday.is_multi_day}
                    onChange={() => setNewHoliday({...newHoliday, is_multi_day: false, end_date: ''})}
                    className="w-4 h-4 text-teal-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Single Day</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="holiday_type"
                    checked={newHoliday.is_multi_day}
                    onChange={() => setNewHoliday({...newHoliday, is_multi_day: true})}
                    className="w-4 h-4 text-teal-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Multiple Days (Date Range)</span>
                </label>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Name *</label>
                  <Input 
                    value={newHoliday.name}
                    onChange={(e) => setNewHoliday({...newHoliday, name: e.target.value})}
                    placeholder="e.g., Eid Ul Fitr Holidays 2026"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {newHoliday.is_multi_day ? 'Start Date *' : 'Date *'}
                  </label>
                  <Input 
                    type="date"
                    value={newHoliday.date}
                    onChange={(e) => setNewHoliday({...newHoliday, date: e.target.value})}
                  />
                </div>
                
                {newHoliday.is_multi_day && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                    <Input 
                      type="date"
                      value={newHoliday.end_date}
                      onChange={(e) => setNewHoliday({...newHoliday, end_date: e.target.value})}
                      min={newHoliday.date}
                    />
                  </div>
                )}
                
                {!newHoliday.is_multi_day && <div></div>}
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <Input 
                    value={newHoliday.description}
                    onChange={(e) => setNewHoliday({...newHoliday, description: e.target.value})}
                    placeholder="Optional description..."
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="office_closed"
                    checked={newHoliday.is_office_closed}
                    onChange={(e) => setNewHoliday({...newHoliday, is_office_closed: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label htmlFor="office_closed" className="text-sm text-gray-700">Office Closed</label>
                </div>
              </div>
              
              {/* Preview for multi-day */}
              {newHoliday.is_multi_day && newHoliday.date && newHoliday.end_date && (
                <div className="mt-4 p-3 bg-teal-50 rounded-lg border border-teal-200">
                  <p className="text-sm text-teal-700">
                    <strong>Preview:</strong> This will create holiday entries for each day from{' '}
                    <strong>{newHoliday.date}</strong> to <strong>{newHoliday.end_date}</strong>
                    {' '}({Math.ceil((new Date(newHoliday.end_date) - new Date(newHoliday.date)) / (1000 * 60 * 60 * 24)) + 1} days)
                  </p>
                </div>
              )}
              
              <div className="flex justify-end gap-3 mt-4">
                <Button variant="outline" onClick={() => setShowNewHoliday(false)}>Cancel</Button>
                <Button onClick={createHoliday} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {newHoliday.is_multi_day ? 'Add Holidays' : 'Add Holiday'}
                </Button>
              </div>
            </div>
          )}

          {/* Holidays List */}
          <div className="bg-white p-6 rounded-xl border shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Holiday Calendar</h3>
              {!showNewHoliday && (
                <Button onClick={() => setShowNewHoliday(true)} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Holiday
                </Button>
              )}
            </div>
            
            {holidays.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No holidays added yet</p>
            ) : (
              <div className="space-y-3">
                {holidays.map(holiday => (
                  <div key={holiday.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className="bg-purple-100 p-3 rounded-lg">
                        <Calendar className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{holiday.name}</p>
                        <p className="text-sm text-gray-500">{holiday.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">{holiday.date}</span>
                      {holiday.is_office_closed && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Office Closed</span>
                      )}
                      <button 
                        onClick={() => deleteHoliday(holiday.id)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Policy Signatures Tab */}
      {activeTab === 'policies' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          {policySignatures && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white border rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-800">{policySignatures.summary.total}</p>
                    <p className="text-sm text-gray-500">Total Employees</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{policySignatures.summary.signed}</p>
                    <p className="text-sm text-gray-500">Signed</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="bg-amber-100 p-3 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{policySignatures.summary.unsigned}</p>
                    <p className="text-sm text-gray-500">Pending Signatures</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="bg-white border rounded-xl p-5">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Policy Signatures</h3>
                <p className="text-sm text-gray-500">
                  Policy Version: {policySignatures?.policy_version || '-'}
                </p>
              </div>
              {policySignatures?.summary?.unsigned > 0 && (
                <Button
                  onClick={() => {
                    const unsignedIds = policySignatures.employees
                      .filter(e => !e.has_signed)
                      .map(e => e.user_id);
                    sendPolicyReminders(unsignedIds);
                  }}
                  disabled={sendingReminders}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  {sendingReminders ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Reminders to All Unsigned
                </Button>
              )}
            </div>

            {/* Employee List */}
            {policySignatures?.employees?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No employees found</p>
            ) : (
              <div className="space-y-2">
                {/* Unsigned First */}
                {policySignatures?.employees
                  ?.filter(e => !e.has_signed)
                  .map(employee => (
                    <div 
                      key={employee.user_id} 
                      className="flex items-center justify-between p-4 border border-amber-200 bg-amber-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="bg-amber-100 p-2 rounded-full">
                          <AlertCircle className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{employee.full_name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">{employee.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm rounded-full font-medium">
                          Not Signed
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendPolicyReminders([employee.user_id])}
                          disabled={sendingReminders}
                          className="border-amber-300 text-amber-700 hover:bg-amber-100"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Remind
                        </Button>
                      </div>
                    </div>
                  ))}
                
                {/* Signed Employees */}
                {policySignatures?.employees
                  ?.filter(e => e.has_signed)
                  .map(employee => (
                    <div 
                      key={employee.user_id} 
                      className="flex items-center justify-between p-4 border border-green-200 bg-green-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="bg-green-100 p-2 rounded-full">
                          <Check className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{employee.full_name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">{employee.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full font-medium">
                            Signed
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            {employee.signed_at ? new Date(employee.signed_at).toLocaleDateString() : '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HRManagement;
